import { parseGIF, decompressFrames } from 'gifuct-js'
import { WIDGET_TYPES } from '../constants.js'

// Mocked data sources for widgets with no wired backend yet (place/weather) - per the
// original scope, these can be mocked initially and swapped for real APIs later.
const MOCK_WEATHER = { temperature: 21, unit: 'celsius', emoji: '☀️' }

export const getWidgetDisplayText = (overlay) => {
    const data = overlay.data || {}
    switch (overlay.widgetType) {
        case WIDGET_TYPES.PLACE:
            return `📍 ${data.location || 'Lieu'}`
        case WIDGET_TYPES.DATETIME: {
            // Defaults to the live current date/time (existing behavior) unless the user picked
            // a specific fixed date/time to display instead (useLiveDateTime === false).
            const value = data.useLiveDateTime === false && data.customDateTime
                ? new Date(data.customDateTime)
                : new Date()
            const format = data.format || 'datetime'
            if (format === 'date') return value.toLocaleDateString()
            if (format === 'time') return value.toLocaleTimeString().slice(0, 5)
            if (format === 'full') {
                return value.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
            }
            return `${value.toLocaleDateString()} ${value.toLocaleTimeString().slice(0, 5)}`
        }
        case WIDGET_TYPES.WEATHER: {
            const temp = data.temperature !== undefined ? data.temperature : MOCK_WEATHER.temperature
            const unit = data.unit === 'fahrenheit' ? '°F' : '°C'
            const value = data.unit === 'fahrenheit' ? Math.round(temp * 9 / 5 + 32) : temp
            const emoji = data.emoji || MOCK_WEATHER.emoji
            return `${emoji} ${value}${unit}`
        }
        case WIDGET_TYPES.MUSIC:
            return `🎵 ${data.title || 'Titre - Artiste'}`
        case WIDGET_TYPES.HASHTAG:
            return `#${(data.tag || 'hashtag').replace(/^#/, '')}`
        case WIDGET_TYPES.MENTION:
            return `@${(data.username || 'utilisateur').replace(/^@/, '')}`
        default:
            return ''
    }
}

const gifCache = new Map()

// Decodes a GIF's frames once (cached by URL) into a list of { canvas, delayMs }.
//
// gifuct-js only ever hands back each frame as its own small changed sub-rectangle (frame.dims.
// left/top/width/height, usually much smaller than the GIF's actual logical size for anything but
// the first frame - most encoders only store what changed) - never a ready-to-draw full frame.
// Treating that patch as if it were the whole image (previous version of this code) drew each
// frame at the wrong size/position with nothing underneath, which is exactly what showed up as the
// animation jumping around and looking corrupted instead of playing cleanly. Reconstructing the
// real frame means compositing each patch, in order, onto one persistent full-size canvas at its
// own offset - the same recipe gifuct-js's own demo uses - and snapshotting that canvas's state
// after each patch as the actual frame to display, respecting each frame's disposal method (0/1:
// leave as-is for the next frame, 2: clear back to transparent, 3: restore to what was there
// right before this frame was drawn) before the next patch is applied.
export const decodeGifFrames = async (url) => {
    if (gifCache.has(url)) return gifCache.get(url)

    const promise = (async () => {
        const response = await fetch(url)
        const buffer = await response.arrayBuffer()
        const gif = parseGIF(buffer)
        const rawFrames = decompressFrames(gif, true)

        const width = gif.lsd.width
        const height = gif.lsd.height

        const stage = document.createElement('canvas')
        stage.width = width
        stage.height = height
        const stageCtx = stage.getContext('2d')

        const patchCanvas = document.createElement('canvas')
        const patchCtx = patchCanvas.getContext('2d')

        let pendingDisposal = null // what to undo on `stage` before the *next* patch is drawn

        const frames = rawFrames.map((frame) => {
            const { left, top, width: fw, height: fh } = frame.dims

            if (pendingDisposal) {
                if (pendingDisposal.disposalType === 2) {
                    stageCtx.clearRect(pendingDisposal.left, pendingDisposal.top, pendingDisposal.width, pendingDisposal.height)
                } else if (pendingDisposal.disposalType === 3 && pendingDisposal.snapshot) {
                    stageCtx.putImageData(pendingDisposal.snapshot, pendingDisposal.left, pendingDisposal.top)
                }
                pendingDisposal = null
            }

            // Disposal type 3 restores this same region to whatever it looked like right before
            // this frame gets drawn - that "before" state has to be captured now, ahead of drawing.
            const snapshot = frame.disposalType === 3 ? stageCtx.getImageData(left, top, fw, fh) : null

            patchCanvas.width = fw
            patchCanvas.height = fh
            const imageData = patchCtx.createImageData(fw, fh)
            imageData.data.set(frame.patch)
            patchCtx.putImageData(imageData, 0, 0)
            stageCtx.drawImage(patchCanvas, left, top)

            const frameCanvas = document.createElement('canvas')
            frameCanvas.width = width
            frameCanvas.height = height
            frameCanvas.getContext('2d').drawImage(stage, 0, 0)

            pendingDisposal = { disposalType: frame.disposalType, left, top, width: fw, height: fh, snapshot }

            return { canvas: frameCanvas, delayMs: frame.delay || 100 }
        })

        const totalDurationMs = frames.reduce((sum, f) => sum + f.delayMs, 0) || 1
        return { frames, totalDurationMs }
    })()

    gifCache.set(url, promise)
    return promise
}

// Returns the canvas for whichever frame should be showing at `elapsedMs` (looping).
export const getGifFrameAt = (gifData, elapsedMs) => {
    if (!gifData || !gifData.frames.length) return null
    const loopedMs = ((elapsedMs % gifData.totalDurationMs) + gifData.totalDurationMs) % gifData.totalDurationMs
    let acc = 0
    for (const frame of gifData.frames) {
        acc += frame.delayMs
        if (loopedMs < acc) return frame.canvas
    }
    return gifData.frames[gifData.frames.length - 1].canvas
}
